/** Phase OO+ — Tiny Web Audio API "pop" synthesizer.
 *
 *  Designed to be triggered on a user gesture (the auto-playing intro counts
 *  as one in most browsers because the AudioContext is created lazily inside
 *  this helper after page interaction). If `AudioContext` is unavailable or
 *  the user has not interacted yet, calls are no-ops — never throws.
 */
let _ctx = null;

function getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
    return _ctx;
  } catch (_) {
    return null;
  }
}

function _resume() {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

/** Play a short "balloon pop" — a snappy attack + filtered noise burst. */
export function playPop({ pitch = 200, volume = 0.35 } = {}) {
  const ctx = _resume();
  if (!ctx) return;
  const now = ctx.currentTime;

  // 1. Click — a short pitched sine that drops fast (gives the "thump")
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(pitch, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 0.08);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(volume, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
  osc.connect(oscGain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);

  // 2. Burst — short filtered white noise (gives the "crack")
  const bufferSize = Math.floor(ctx.sampleRate * 0.06);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1200;
  bp.Q.value = 1.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(volume * 0.9, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  noise.connect(bp).connect(noiseGain).connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.1);
}

/** Soft "shimmer" — used when the PYS title reveals. */
export function playShimmer({ volume = 0.18 } = {}) {
  const ctx = _resume();
  if (!ctx) return;
  const now = ctx.currentTime;
  // Two stacked sines that rise in pitch + fade in then out — magical chime
  [660, 990].forEach((startHz, i) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(startHz, now + i * 0.05);
    osc.frequency.linearRampToValueAtTime(startHz * 1.5, now + 1.0 + i * 0.05);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now + i * 0.05);
    g.gain.exponentialRampToValueAtTime(volume, now + 0.25 + i * 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 1.2 + i * 0.05);
    osc.connect(g).connect(ctx.destination);
    osc.start(now + i * 0.05);
    osc.stop(now + 1.3 + i * 0.05);
  });
}
